import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  influencerProfile,
  factoryPartners,
  salesRecords,
  monthlyEarnings,
  platformStats,
} from "@/mocks/influencer";
import SeoHead from "@/components/feature/SeoHead";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://www.crownhongyunmall.com";

const influencerJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "达人分销中心 | 抖音TikTok微信YouTube带货赚佣金 | 皇冠鸿运商城",
    "description": "皇冠鸿运商城达人分销中心，抖音、TikTok、微信视频号、YouTube达人均可加入，选品推广，工厂直发，按比例赚取佣金（8%–20%），每月1日结算，零库存风险。",
    "url": `${SITE_URL}/influencer-dashboard`,
    "inLanguage": "zh-CN",
    "isPartOf": {
      "@type": "WebSite",
      "name": "皇冠鸿运商城",
      "url": SITE_URL,
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "达人分销中心", "item": `${SITE_URL}/influencer-dashboard` },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "皇冠鸿运商城达人分销计划",
    "description": "抖音、TikTok、微信视频号、YouTube达人加入分销计划，选择工厂直营商品推广，工厂代发货（Dropshipping），按订单金额8%–20%比例赚取佣金，每月1日统一结算。",
    "url": `${SITE_URL}/influencer-dashboard`,
    "provider": {
      "@type": "Organization",
      "name": "皇冠鸿运商城",
      "url": SITE_URL,
    },
    "serviceType": "达人分销 / 网红带货",
    "areaServed": {
      "@type": "Country",
      "name": "China",
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "CNY",
      "description": "永久免费加入，无需备货，零库存风险",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "达人分销需要缴纳费用吗？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "完全免费，永久免费，无任何隐藏费用。注册账号后申请加入达人分销计划，24小时内审核通过即可开始推广赚佣金。",
        },
      },
      {
        "@type": "Question",
        "name": "达人佣金比例是多少？什么时候结算？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "各工厂自主设定佣金比例，通常在8%–20%之间。每月1日统一结算上月佣金，可通过支付宝、微信支付、银行转账或PayPal提现。",
        },
      },
      {
        "@type": "Question",
        "name": "达人需要自己备货发货吗？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "不需要。粉丝下单后，工厂从本地仓库直接发货（代发货/Dropshipping），达人无需备货，零库存风险，专注内容创作和推广即可。",
        },
      },
    ],
  },
];

type TabType = "overview" | "sales" | "catalog" | "factories" | "platforms" | "payout";

const maxEarnings = Math.max(...monthlyEarnings.map((d) => d.amount));

export default function InfluencerDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [platformFilter, setPlatformFilter] = useState<string>("全部");
  const [factoryFilter, setFactoryFilter] = useState<string>("全部");
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState(String(influencerProfile.pendingPayout));

  const platforms = ["全部", "抖音", "TikTok", "微信视频号", "YouTube"];
  const factories = ["全部", ...factoryPartners.map((f) => f.name)];

  const [catalogCategory, setCatalogCategory] = useState("全部");
  const [catalogFactory, setCatalogFactory] = useState("全部");
  const [copiedLink, setCopiedLink] = useState<string | null>(null);

  const catalogProducts = [
    { id: "P001", name: "华强数码 无线蓝牙耳机 Pro Max", price: 299, originalPrice: 499, factory: "深圳华强电子科技有限公司", factoryId: "FAC-001", brand: "华强数码", category: "手机数码", commissionRate: 8, sales: 3240, image: "https://public.readdy.ai/ai/img_res/e5db8c809c4f80581677e6a9a9b4fe71.jpg" },
    { id: "P002", name: "华强数码 智能手表 运动健康版", price: 599, originalPrice: 899, factory: "深圳华强电子科技有限公司", factoryId: "FAC-001", brand: "华强数码", category: "手机数码", commissionRate: 8, sales: 1820, image: "https://readdy.ai/api/search-image?query=smart%20watch%20sports%20health%20monitoring%20wearable%20device%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20ultra%20realistic&width=200&height=200&seq=cat_p2&orientation=squarish" },
    { id: "P003", name: "华强数码 Type-C 快充数据线 3条装", price: 59, originalPrice: 99, factory: "深圳华强电子科技有限公司", factoryId: "FAC-001", brand: "华强数码", category: "手机数码", commissionRate: 8, sales: 12400, image: "https://readdy.ai/api/search-image?query=USB%20Type-C%20fast%20charging%20cable%20set%203%20pack%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20ultra%20realistic&width=200&height=200&seq=cat_p3&orientation=squarish" },
    { id: "P004", name: "美颜集 玻尿酸精华液 50ml 补水保湿", price: 388, originalPrice: 580, factory: "广州美妆集团股份有限公司", factoryId: "FAC-002", brand: "美颜集", category: "美妆护肤", commissionRate: 15, sales: 8900, image: "https://readdy.ai/api/search-image?query=hyaluronic%20acid%20serum%20skincare%20bottle%20luxury%20packaging%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20ultra%20realistic&width=200&height=200&seq=cat_p4&orientation=squarish" },
    { id: "P005", name: "美颜集 防晒霜 SPF50+ PA++++ 50g", price: 168, originalPrice: 260, factory: "广州美妆集团股份有限公司", factoryId: "FAC-002", brand: "美颜集", category: "美妆护肤", commissionRate: 15, sales: 15600, image: "https://readdy.ai/api/search-image?query=sunscreen%20SPF50%20PA%20%20%20%20%20skincare%20tube%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20ultra%20realistic&width=200&height=200&seq=cat_p5&orientation=squarish" },
    { id: "P006", name: "美颜集 氨基酸洁面乳 150ml 温和清洁", price: 128, originalPrice: 198, factory: "广州美妆集团股份有限公司", factoryId: "FAC-002", brand: "美颜集", category: "美妆护肤", commissionRate: 15, sales: 22100, image: "https://readdy.ai/api/search-image?query=amino%20acid%20facial%20cleanser%20foam%20skincare%20tube%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20ultra%20realistic&width=200&height=200&seq=cat_p6&orientation=squarish" },
    { id: "P007", name: "杭韵时尚 法式碎花连衣裙 夏季新款", price: 299, originalPrice: 499, factory: "杭州时尚服饰制造有限公司", factoryId: "FAC-003", brand: "杭韵时尚", category: "服装鞋包", commissionRate: 12, sales: 6700, image: "https://readdy.ai/api/search-image?query=French%20floral%20summer%20dress%20elegant%20fashion%20clothing%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20ultra%20realistic&width=200&height=200&seq=cat_p7&orientation=squarish" },
    { id: "P008", name: "杭韵时尚 修身西装套装 男士商务", price: 899, originalPrice: 1399, factory: "杭州时尚服饰制造有限公司", factoryId: "FAC-003", brand: "杭韵时尚", category: "服装鞋包", commissionRate: 12, sales: 3200, image: "https://readdy.ai/api/search-image?query=slim%20fit%20business%20suit%20set%20mens%20fashion%20clothing%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20ultra%20realistic&width=200&height=200&seq=cat_p8&orientation=squarish" },
    { id: "P009", name: "杭韵时尚 羊绒混纺圆领毛衣 男女同款", price: 359, originalPrice: 599, factory: "杭州时尚服饰制造有限公司", factoryId: "FAC-003", brand: "杭韵时尚", category: "服装鞋包", commissionRate: 12, sales: 9800, image: "https://readdy.ai/api/search-image?query=cashmere%20blend%20crew%20neck%20sweater%20knitwear%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20ultra%20realistic&width=200&height=200&seq=cat_p9&orientation=squarish" },
    { id: "P010", name: "佛山优居 北欧实木茶几 原木色", price: 1299, originalPrice: 1999, factory: "佛山家居用品制造厂", factoryId: "FAC-004", brand: "佛山优居", category: "家居家具", commissionRate: 10, sales: 1240, image: "https://readdy.ai/api/search-image?query=Nordic%20solid%20wood%20coffee%20table%20natural%20oak%20color%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20ultra%20realistic&width=200&height=200&seq=cat_p10&orientation=squarish" },
    { id: "P011", name: "佛山优居 全身落地镜 金属细框 180cm", price: 599, originalPrice: 899, factory: "佛山家居用品制造厂", factoryId: "FAC-004", brand: "佛山优居", category: "家居家具", commissionRate: 10, sales: 4500, image: "https://readdy.ai/api/search-image?query=full%20length%20floor%20mirror%20thin%20gold%20metal%20frame%20modern%20home%20decor%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20ultra%20realistic&width=200&height=200&seq=cat_p11&orientation=squarish" },
    { id: "P012", name: "华强数码 便携充电宝 20000mAh 快充", price: 189, originalPrice: 299, factory: "深圳华强电子科技有限公司", factoryId: "FAC-001", brand: "华强数码", category: "手机数码", commissionRate: 8, sales: 7800, image: "https://readdy.ai/api/search-image?query=portable%20power%20bank%2020000mAh%20fast%20charging%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20ultra%20realistic&width=200&height=200&seq=cat_p12&orientation=squarish" },
  ];

  const catalogCategories = ["全部", "手机数码", "美妆护肤", "服装鞋包", "家居家具"];
  const catalogFactoryNames = ["全部", ...factoryPartners.map(f => f.brand)];

  const filteredCatalog = catalogProducts.filter(p => {
    const matchCat = catalogCategory === "全部" || p.category === catalogCategory;
    const matchFac = catalogFactory === "全部" || p.brand === catalogFactory;
    return matchCat && matchFac;
  });

  const handleCopyLink = (productId: string) => {
    const link = `https://crown-hongyun.com/product/${productId}?ref=${influencerProfile.id}`;
    navigator.clipboard.writeText(link).catch(() => {});
    setCopiedLink(productId);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const filteredSales = salesRecords.filter((r) => {
    const matchPlatform = platformFilter === "全部" || r.platform === platformFilter;
    const matchFactory = factoryFilter === "全部" || r.factory === factoryFilter;
    return matchPlatform && matchFactory;
  });

  const handleWithdraw = () => {
    setWithdrawSuccess(true);
    setTimeout(() => {
      setWithdrawSuccess(false);
      setWithdrawOpen(false);
    }, 2200);
  };

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <SeoHead
        title="达人分销中心 | 抖音TikTok微信YouTube带货赚佣金 | 皇冠鸿运商城"
        description="皇冠鸿运商城达人分销中心，抖音、TikTok、微信视频号、YouTube达人均可加入，选品推广，工厂直发，按比例赚取佣金（8%–20%），每月1日结算，零库存风险，永久免费。"
        keywords="达人分销,网红带货,抖音带货,TikTok带货,佣金赚钱,工厂直发,皇冠鸿运商城"
        canonical="/influencer-dashboard"
        jsonLd={influencerJsonLd}
      />
      {/* Top bar */}
      <div className="bg-gradient-to-r from-amber-600 to-yellow-500 text-white text-xs py-1.5 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <span>皇冠鸿运商城 — 达人分销后台</span>
          <a href="/" className="hover:underline cursor-pointer flex items-center gap-1">
            <i className="ri-home-line"></i> 返回首页
          </a>
        </div>
      </div>

      {/* Nav */}
      <nav className="bg-white border-b border-amber-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-2 cursor-pointer">
              <img
                src="https://public.readdy.ai/ai/img_res/f704ac6a-a45e-41ab-b34d-a4b4313691a9.png"
                alt="Logo"
                className="h-9 w-auto object-contain"
              />
              <span
                className="text-red-600 font-bold text-lg hidden sm:block whitespace-nowrap"
                style={{ fontFamily: "'Noto Serif SC', serif" }}
              >
                皇冠鸿运商城
              </span>
            </a>
            <span className="text-gray-300 hidden sm:block">|</span>
            <span className="text-amber-600 text-sm font-semibold hidden sm:block">达人佣金中心</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <img
                src={influencerProfile.avatar}
                alt={influencerProfile.name}
                className="w-9 h-9 rounded-full object-cover object-top flex-shrink-0"
              />
              <div className="hidden sm:block">
                <p className="text-gray-800 font-semibold text-sm leading-none">{influencerProfile.name}</p>
                <p className="text-amber-500 text-xs mt-0.5 flex items-center gap-1">
                  <i className={influencerProfile.levelIcon}></i>
                  {influencerProfile.level}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/")}
              className="text-gray-400 hover:text-amber-500 cursor-pointer text-sm whitespace-nowrap"
            >
              <i className="ri-logout-box-line"></i>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Hero card */}
        <div className="relative bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-600 rounded-2xl p-6 mb-6 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=golden%20luxury%20abstract%20background%20with%20shimmering%20light%20rays%20and%20bokeh%20particles%2C%20warm%20amber%20tones%2C%20premium%20texture%2C%20no%20text&width=1200&height=300&seq=inf_hero_bg&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div className="flex items-center gap-4">
              <img
                src={influencerProfile.avatar}
                alt={influencerProfile.name}
                className="w-16 h-16 rounded-2xl object-cover object-top flex-shrink-0 border-2 border-white/40"
              />
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-white/20 text-white text-xs px-2.5 py-0.5 rounded-full font-medium flex items-center gap-1">
                    <i className={influencerProfile.levelIcon}></i>
                    {influencerProfile.level}
                  </span>
                  <span className="text-white/60 text-xs">ID: {influencerProfile.id}</span>
                </div>
                <h1
                  className="text-white font-bold text-2xl"
                  style={{ fontFamily: "'Noto Serif SC', serif" }}
                >
                  {influencerProfile.name}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  {influencerProfile.platforms.map((p) => (
                    <span
                      key={p.name}
                      className="flex items-center gap-1 bg-white/15 text-white text-xs px-2 py-0.5 rounded-full"
                    >
                      <i className={p.icon}></i>
                      {p.name} {p.followers}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-start sm:items-end gap-2">
              <div className="text-right">
                <p className="text-white/70 text-xs">本月佣金收入</p>
                <p className="text-white font-bold text-3xl">
                  ¥{influencerProfile.monthEarnings.toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => setWithdrawOpen(true)}
                className="bg-white text-amber-600 px-5 py-2 rounded-xl font-bold text-sm transition-all hover:bg-white/90 cursor-pointer whitespace-nowrap shadow-sm"
              >
                <i className="ri-bank-card-line mr-1"></i>
                申请提现 ¥{influencerProfile.pendingPayout.toLocaleString()}
              </button>
            </div>
          </div>

          {/* Stats strip */}
          <div className="relative mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "累计总收益", value: `¥${influencerProfile.totalEarnings.toLocaleString()}`, icon: "ri-money-cny-circle-line" },
              { label: "累计销售订单", value: `${influencerProfile.totalOrders.toLocaleString()}单`, icon: "ri-shopping-bag-line" },
              { label: "本月订单", value: `${influencerProfile.monthOrders}单`, icon: "ri-bar-chart-line" },
              { label: "综合转化率", value: `${influencerProfile.conversionRate}%`, icon: "ri-line-chart-line" },
            ].map((s) => (
              <div key={s.label} className="bg-white/15 rounded-xl px-4 py-3 text-white">
                <div className="flex items-center gap-1.5 mb-1">
                  <i className={`${s.icon} text-white/70 text-sm`}></i>
                  <span className="text-white/70 text-xs">{s.label}</span>
                </div>
                <p className="font-bold text-lg">{s.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl overflow-hidden">
          <div className="flex border-b border-gray-100 overflow-x-auto">
            {(
              [
                { id: "overview", label: "收益概览", icon: "ri-bar-chart-2-line" },
                { id: "sales", label: "销售记录", icon: "ri-shopping-cart-line" },
                { id: "catalog", label: "商品选品库", icon: "ri-store-2-line", gold: true },
                { id: "factories", label: "合作工厂", icon: "ri-building-line" },
                { id: "platforms", label: "平台数据", icon: "ri-global-line" },
                { id: "payout", label: "提现记录", icon: "ri-bank-card-line" },
              ] as { id: TabType; label: string; icon: string; gold?: boolean }[]
            ).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? tab.gold ? "text-amber-500 border-b-2 border-amber-500" : "text-amber-500 border-b-2 border-amber-500"
                    : tab.gold ? "text-amber-500 hover:text-amber-600" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* ── Overview Tab ── */}
            {activeTab === "overview" && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-gray-800 font-bold text-lg">近6个月收益趋势</h2>
                  <span className="text-gray-400 text-xs">各工厂佣金比例不同，详见「合作工厂」</span>
                </div>

                {/* Bar chart */}
                <div className="flex items-end gap-3 h-52 mb-2">
                  {monthlyEarnings.map((d) => {
                    const pct = (d.amount / maxEarnings) * 100;
                    const isLast = d.month === "3月";
                    return (
                      <div key={d.month} className="flex-1 flex flex-col items-center gap-2">
                        <div className="text-center">
                          <span className="text-gray-600 text-xs font-medium block">
                            ¥{(d.amount / 1000).toFixed(1)}k
                          </span>
                          <span className="text-gray-400 text-xs">{d.orders}单</span>
                        </div>
                        <div className="w-full relative" style={{ height: `${pct}%`, minHeight: "20px" }}>
                          <div
                            className={`w-full h-full rounded-t-lg transition-all duration-500 ${
                              isLast
                                ? "bg-gradient-to-t from-amber-500 to-yellow-400"
                                : "bg-gradient-to-t from-amber-200 to-yellow-100"
                            }`}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-3 mb-8">
                  {monthlyEarnings.map((d) => (
                    <div key={d.month} className="flex-1 text-center text-xs text-gray-400">
                      {d.month}
                    </div>
                  ))}
                </div>

                {/* Factory commission breakdown */}
                <h3 className="text-gray-700 font-bold text-sm mb-4">各工厂佣金收益分布</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {factoryPartners
                    .filter((f) => f.status === "合作中")
                    .map((f) => {
                      const total = factoryPartners
                        .filter((fp) => fp.status === "合作中")
                        .reduce((sum, fp) => sum + fp.myEarnings, 0);
                      const pct = Math.round((f.myEarnings / total) * 100);
                      return (
                        <div key={f.id} className="bg-amber-50 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <img
                              src={f.logo}
                              alt={f.brand}
                              className="w-8 h-8 rounded-lg object-cover object-top flex-shrink-0"
                            />
                            <div className="min-w-0">
                              <p className="text-gray-800 font-semibold text-xs truncate">{f.brand}</p>
                              <p className="text-amber-600 text-xs font-bold">佣金 {f.commissionRate}%</p>
                            </div>
                          </div>
                          <p className="text-gray-900 font-bold text-xl mb-2">
                            ¥{f.myEarnings.toLocaleString()}
                          </p>
                          <div className="bg-amber-100 rounded-full h-1.5">
                            <div
                              className="bg-gradient-to-r from-amber-400 to-yellow-500 h-1.5 rounded-full"
                              style={{ width: `${pct}%` }}
                            ></div>
                          </div>
                          <p className="text-gray-400 text-xs mt-1">{pct}% 占比</p>
                        </div>
                      );
                    })}
                </div>

                {/* How it works */}
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-100 rounded-xl p-5">
                  <h3 className="text-gray-800 font-bold text-sm mb-4 flex items-center gap-2">
                    <i className="ri-information-line text-amber-500"></i>
                    达人分销 · 运作说明
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      {
                        step: "01",
                        title: "选品推广",
                        desc: "从合作工厂的商品库中选择适合你粉丝的商品，在抖音、TikTok、微信视频号、YouTube等平台发布推广内容",
                        icon: "ri-megaphone-line",
                      },
                      {
                        step: "02",
                        title: "工厂直发",
                        desc: "粉丝下单后，工厂从本地仓库直接发货（代发货/Dropshipping），你无需备货，零库存风险",
                        icon: "ri-truck-line",
                      },
                      {
                        step: "03",
                        title: "按比例结算",
                        desc: "每笔订单按工厂设定的佣金比例（8%~18%不等）自动计算，每月1日统一结算到账",
                        icon: "ri-money-cny-circle-line",
                      },
                    ].map((item) => (
                      <div key={item.step} className="flex gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-xl flex-shrink-0">
                          <i className={`${item.icon} text-white text-lg`}></i>
                        </div>
                        <div>
                          <p className="text-amber-600 text-xs font-bold mb-0.5">STEP {item.step}</p>
                          <p className="text-gray-800 font-semibold text-sm mb-1">{item.title}</p>
                          <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Catalog Tab ── */}
            {activeTab === "catalog" && (
              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
                  <div>
                    <h2 className="text-gray-800 font-bold text-lg">商品选品库</h2>
                    <p className="text-gray-400 text-sm mt-0.5">选择商品，一键生成专属推广链接，分享到各平台即可赚取佣金</p>
                  </div>
                  <span className="text-amber-600 text-sm font-medium bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full whitespace-nowrap">
                    <i className="ri-store-2-line mr-1"></i>{filteredCatalog.length} 款商品
                  </span>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-5">
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-gray-400 text-xs whitespace-nowrap">品类：</span>
                    {catalogCategories.map(c => (
                      <button key={c} onClick={() => setCatalogCategory(c)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${catalogCategory === c ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-gray-400 text-xs whitespace-nowrap">工厂：</span>
                    {catalogFactoryNames.map(f => (
                      <button key={f} onClick={() => setCatalogFactory(f)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${catalogFactory === f ? "bg-amber-100 text-amber-600 border border-amber-300" : "bg-gray-50 text-gray-500 border border-gray-200 hover:border-gray-300"}`}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Product grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredCatalog.map(p => {
                    const factory = factoryPartners.find(f => f.id === p.factoryId);
                    const myCommission = (p.price * p.commissionRate / 100).toFixed(2);
                    const isCopied = copiedLink === p.id;
                    return (
                      <div key={p.id} className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-amber-200 transition-all">
                        <div className="relative">
                          <img src={p.image} alt={p.name} className="w-full h-44 object-cover object-top" />
                          <div className="absolute top-2 left-2">
                            <span className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                              佣金 {p.commissionRate}%
                            </span>
                          </div>
                          <div className="absolute top-2 right-2">
                            <span className="bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
                              已售 {p.sales.toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <div className="p-4">
                          <p className="text-gray-800 font-semibold text-sm leading-tight line-clamp-2 mb-2">{p.name}</p>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-red-500 font-bold text-lg">¥{p.price}</span>
                            <span className="text-gray-300 line-through text-xs">¥{p.originalPrice}</span>
                          </div>
                          <div className="flex items-center gap-2 mb-3">
                            <img src={factory?.logo} alt={p.brand} className="w-5 h-5 rounded object-cover object-top flex-shrink-0" />
                            <span className="text-gray-400 text-xs">{p.brand}</span>
                            <span className="text-gray-300">·</span>
                            <span className="text-gray-400 text-xs">{factory?.warehouseCity}仓发货</span>
                          </div>
                          <div className="bg-amber-50 rounded-lg px-3 py-2 mb-3 flex items-center justify-between">
                            <span className="text-gray-500 text-xs">每单我的佣金</span>
                            <span className="text-amber-600 font-bold text-sm">+¥{myCommission}</span>
                          </div>
                          <button
                            onClick={() => handleCopyLink(p.id)}
                            className={`w-full py-2.5 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-all ${isCopied ? "bg-green-500 text-white" : "bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-white"}`}
                          >
                            {isCopied
                              ? <><i className="ri-check-line mr-1"></i>链接已复制！</>
                              : <><i className="ri-links-line mr-1"></i>生成专属推广链接</>
                            }
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {filteredCatalog.length === 0 && (
                  <div className="text-center py-16 text-gray-400">
                    <i className="ri-inbox-line text-4xl mb-2 block"></i>
                    <p className="text-sm">暂无符合条件的商品</p>
                  </div>
                )}
              </div>
            )}

            {/* ── Sales Tab ── */}
            {activeTab === "sales" && (
              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
                  <h2 className="text-gray-800 font-bold text-lg">销售记录</h2>
                  <div className="flex flex-wrap gap-2">
                    {/* Platform filter */}
                    <div className="flex items-center gap-1 flex-wrap">
                      {platforms.map((p) => (
                        <button
                          key={p}
                          onClick={() => setPlatformFilter(p)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                            platformFilter === p
                              ? "bg-amber-500 text-white"
                              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Factory filter */}
                <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
                  <span className="text-gray-400 text-xs whitespace-nowrap">工厂筛选：</span>
                  {factories.map((f) => (
                    <button
                      key={f}
                      onClick={() => setFactoryFilter(f)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        factoryFilter === f
                          ? "bg-amber-100 text-amber-600 border border-amber-300"
                          : "bg-gray-50 text-gray-500 border border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {f === "全部" ? "全部工厂" : factoryPartners.find((fp) => fp.name === f)?.brand ?? f}
                    </button>
                  ))}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        {["商品", "平台", "工厂 / 仓库", "订单金额", "佣金比例", "我的佣金", "状态"].map(
                          (h) => (
                            <th
                              key={h}
                              className="text-left text-gray-400 text-xs font-medium pb-3 pr-4 whitespace-nowrap"
                            >
                              {h}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSales.map((r) => (
                        <tr
                          key={r.id}
                          className="border-b border-gray-50 hover:bg-amber-50/40 transition-colors"
                        >
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2">
                              <img
                                src={r.productImage}
                                alt={r.product}
                                className="w-10 h-10 rounded-lg object-cover object-top flex-shrink-0"
                              />
                              <div className="min-w-0">
                                <p className="text-gray-700 font-medium text-xs leading-tight line-clamp-2 max-w-[160px]">
                                  {r.product}
                                </p>
                                <p className="text-gray-400 text-xs mt-0.5">{r.date}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 pr-4">
                            <span
                              className={`inline-flex items-center gap-1 text-white text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${r.platformColor}`}
                            >
                              <i className={r.platformIcon}></i>
                              {r.platform}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <p className="text-gray-600 text-xs whitespace-nowrap">
                              {factoryPartners.find((f) => f.id === r.factoryId)?.brand ?? r.factory}
                            </p>
                            <p className="text-gray-400 text-xs flex items-center gap-1 mt-0.5">
                              <i className="ri-map-pin-line"></i>
                              {r.warehouseCity}仓发货
                            </p>
                          </td>
                          <td className="py-3 pr-4">
                            <span className="text-gray-800 font-semibold text-sm">
                              ¥{r.orderAmount.toLocaleString()}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className="bg-amber-100 text-amber-600 text-xs px-2 py-0.5 rounded-full font-bold whitespace-nowrap">
                              {r.commissionRate}%
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className="text-amber-600 font-bold text-sm">
                              +¥{r.commission.toFixed(2)}
                            </span>
                          </td>
                          <td className="py-3">
                            <span
                              className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${
                                r.status === "已结算"
                                  ? "bg-green-100 text-green-600"
                                  : "bg-orange-100 text-orange-500"
                              }`}
                            >
                              {r.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredSales.length === 0 && (
                    <div className="text-center py-12 text-gray-400">
                      <i className="ri-inbox-line text-4xl mb-2 block"></i>
                      <p className="text-sm">暂无符合条件的销售记录</p>
                    </div>
                  )}
                </div>

                {/* Summary row */}
                <div className="mt-4 flex flex-wrap gap-4 bg-amber-50 rounded-xl p-4">
                  <div>
                    <p className="text-gray-400 text-xs">筛选结果订单数</p>
                    <p className="text-gray-800 font-bold text-lg">{filteredSales.length} 单</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">筛选结果总销售额</p>
                    <p className="text-gray-800 font-bold text-lg">
                      ¥{filteredSales.reduce((s, r) => s + r.orderAmount, 0).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">筛选结果总佣金</p>
                    <p className="text-amber-600 font-bold text-lg">
                      ¥{filteredSales.reduce((s, r) => s + r.commission, 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ── Factories Tab ── */}
            {activeTab === "factories" && (
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-gray-800 font-bold text-lg">合作工厂</h2>
                  <span className="text-gray-400 text-xs">各工厂自主设定佣金比例</span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {factoryPartners.map((f) => (
                    <div
                      key={f.id}
                      className={`rounded-2xl border p-5 transition-all ${
                        f.status === "合作中"
                          ? "border-amber-200 bg-white hover:border-amber-300"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <img
                          src={f.logo}
                          alt={f.brand}
                          className="w-14 h-14 rounded-xl object-cover object-top flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="text-gray-800 font-bold text-base">{f.brand}</h3>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                                f.status === "合作中"
                                  ? "bg-green-100 text-green-600"
                                  : "bg-orange-100 text-orange-500"
                              }`}
                            >
                              {f.status}
                            </span>
                          </div>
                          <p className="text-gray-400 text-xs truncate">{f.name}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full">
                              <i className="ri-price-tag-3-line mr-1"></i>{f.category}
                            </span>
                            <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full">
                              <i className="ri-map-pin-line mr-1"></i>{f.warehouseCity}仓
                            </span>
                            <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full">
                              <i className="ri-truck-line mr-1"></i>{f.shippingTime}
                            </span>
                          </div>
                        </div>
                        {/* Commission badge */}
                        <div className="flex-shrink-0 text-center">
                          <div className="bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl px-4 py-2">
                            <p className="text-white/80 text-xs">佣金比例</p>
                            <p className="text-white font-bold text-2xl">{f.commissionRate}%</p>
                          </div>
                        </div>
                      </div>

                      {f.status === "合作中" && (
                        <div className="mt-4 grid grid-cols-3 gap-3 pt-4 border-t border-amber-100">
                          <div className="text-center">
                            <p className="text-gray-400 text-xs">在售商品</p>
                            <p className="text-gray-800 font-bold text-lg">{f.products}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-400 text-xs">我的销量</p>
                            <p className="text-gray-800 font-bold text-lg">{f.totalSales.toLocaleString()}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-400 text-xs">我的佣金</p>
                            <p className="text-amber-600 font-bold text-lg">
                              ¥{f.myEarnings.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      )}

                      {f.status === "待审核" && (
                        <div className="mt-4 bg-orange-50 rounded-xl p-3 text-center">
                          <p className="text-orange-500 text-xs">
                            <i className="ri-time-line mr-1"></i>
                            合作申请审核中，预计 1–3 个工作日完成
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Apply new factory */}
                <div className="mt-5 bg-gradient-to-r from-amber-50 to-yellow-50 border border-dashed border-amber-300 rounded-2xl p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-xl mx-auto mb-3">
                    <i className="ri-add-line text-amber-500 text-2xl"></i>
                  </div>
                  <p className="text-gray-700 font-semibold text-sm mb-1">申请合作更多工厂</p>
                  <p className="text-gray-400 text-xs mb-4">
                    平台已入驻 2,400+ 家正规工厂，各工厂自主设定佣金比例（通常 8%–20%）
                  </p>
                  <button className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2.5 rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-search-line mr-1"></i>浏览工厂商品库
                  </button>
                </div>
              </div>
            )}

            {/* ── Platforms Tab ── */}
            {activeTab === "platforms" && (
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-gray-800 font-bold text-lg">各平台销售数据</h2>
                  <span className="text-gray-400 text-xs">数据统计周期：近30天</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {platformStats.map((p) => (
                    <div key={p.platform} className="bg-white border border-gray-100 rounded-2xl p-5">
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className={`w-12 h-12 flex items-center justify-center ${p.color} rounded-xl flex-shrink-0`}
                        >
                          <i className={`${p.icon} text-white text-xl`}></i>
                        </div>
                        <div>
                          <h3 className="text-gray-800 font-bold text-base">{p.platform}</h3>
                          <p className="text-gray-400 text-xs">
                            {influencerProfile.platforms.find((pl) => pl.name === p.platform)?.handle}
                          </p>
                        </div>
                        <div className="ml-auto text-right">
                          <p className="text-gray-400 text-xs">粉丝数</p>
                          <p className="text-gray-700 font-bold text-sm">
                            {influencerProfile.platforms.find((pl) => pl.name === p.platform)?.followers}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-gray-50 rounded-xl p-3 text-center">
                          <p className="text-gray-400 text-xs mb-1">销售订单</p>
                          <p className="text-gray-800 font-bold text-lg">{p.orders.toLocaleString()}</p>
                        </div>
                        <div className="bg-amber-50 rounded-xl p-3 text-center">
                          <p className="text-gray-400 text-xs mb-1">佣金收益</p>
                          <p className="text-amber-600 font-bold text-lg">
                            ¥{(p.earnings / 1000).toFixed(1)}k
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3 text-center">
                          <p className="text-gray-400 text-xs mb-1">转化率</p>
                          <p className="text-gray-800 font-bold text-lg">{p.conversionRate}%</p>
                        </div>
                      </div>
                      {/* Earnings bar */}
                      <div className="mt-3">
                        <div className="bg-gray-100 rounded-full h-1.5">
                          <div
                            className="bg-gradient-to-r from-amber-400 to-yellow-500 h-1.5 rounded-full transition-all duration-500"
                            style={{
                              width: `${(p.earnings / Math.max(...platformStats.map((ps) => ps.earnings))) * 100}%`,
                            }}
                          ></div>
                        </div>
                        <p className="text-gray-400 text-xs mt-1">
                          占总收益{" "}
                          {Math.round(
                            (p.earnings / platformStats.reduce((s, ps) => s + ps.earnings, 0)) * 100
                          )}
                          %
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tips */}
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
                  <h3 className="text-gray-800 font-bold text-sm mb-3 flex items-center gap-2">
                    <i className="ri-lightbulb-line text-amber-500"></i>
                    提升各平台转化率的小技巧
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { platform: "抖音 / TikTok", tip: "发布15–60秒短视频，展示商品使用场景，结尾引导点击购物车链接" },
                      { platform: "微信视频号", tip: "结合朋友圈图文推广，利用熟人社交关系提升信任度和转化率" },
                      { platform: "YouTube", tip: "制作深度开箱评测视频，在视频描述中放置专属推广链接" },
                      { platform: "通用技巧", tip: "选择工厂佣金比例高（15%+）且客单价适中的商品，收益最大化" },
                    ].map((item) => (
                      <div key={item.platform} className="flex gap-2">
                        <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <div>
                          <span className="text-amber-600 font-semibold text-xs">{item.platform}：</span>
                          <span className="text-gray-500 text-xs">{item.tip}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Payout Tab ── */}
            {activeTab === "payout" && (
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-gray-800 font-bold text-lg">提现记录</h2>
                  <button
                    onClick={() => setWithdrawOpen(true)}
                    className="bg-amber-500 hover:bg-amber-600 text-white px-5 py-2 rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-bank-card-line mr-1"></i>申请提现
                  </button>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl">
                      <i className="ri-time-line text-amber-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-gray-800 font-semibold text-sm">待提现余额</p>
                      <p className="text-gray-400 text-xs">每月1日自动结算，可随时申请提现</p>
                    </div>
                  </div>
                  <p className="text-amber-600 font-bold text-2xl">
                    ¥{influencerProfile.pendingPayout.toLocaleString()}
                  </p>
                </div>

                <div className="space-y-3">
                  {[
                    { id: "PAY-INF-20260401", date: "2026-04-01", amount: 10400, status: "已到账", method: "支付宝", note: "3月佣金结算" },
                    { id: "PAY-INF-20260301", date: "2026-03-01", amount: 7600, status: "已到账", method: "微信支付", note: "2月佣金结算" },
                    { id: "PAY-INF-20260201", date: "2026-02-01", amount: 9200, status: "已到账", method: "支付宝", note: "1月佣金结算" },
                    { id: "PAY-INF-20260101", date: "2026-01-01", amount: 5800, status: "已到账", method: "银行转账", note: "12月佣金结算" },
                    { id: "PAY-INF-20251201", date: "2025-12-01", amount: 3200, status: "已到账", method: "支付宝", note: "11月佣金结算" },
                  ].map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-xl flex-shrink-0">
                          <i className="ri-check-line text-green-500 text-lg"></i>
                        </div>
                        <div>
                          <p className="text-gray-800 font-medium text-sm">{p.note}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span className="text-gray-400 text-xs">{p.date}</span>
                            <span className="text-gray-300">·</span>
                            <span className="text-gray-400 text-xs">{p.method}</span>
                            <span className="text-gray-300">·</span>
                            <span className="text-gray-400 text-xs">{p.id}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-green-500 font-bold text-lg">+¥{p.amount.toLocaleString()}</p>
                        <span className="text-green-500 text-xs bg-green-100 px-2 py-0.5 rounded-full">
                          {p.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Withdraw Modal */}
      {withdrawOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setWithdrawOpen(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-gray-800 font-bold text-lg">申请提现</h3>
              <button
                onClick={() => setWithdrawOpen(false)}
                className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 mb-5 text-center">
              <p className="text-gray-500 text-xs mb-1">可提现金额</p>
              <p className="text-amber-600 font-bold text-3xl">
                ¥{influencerProfile.pendingPayout.toLocaleString()}
              </p>
            </div>
            <div className="space-y-3 mb-5">
              <div>
                <label className="text-gray-600 text-xs font-medium block mb-1.5">提现方式</label>
                <select className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 outline-none cursor-pointer">
                  <option>支付宝</option>
                  <option>微信支付</option>
                  <option>银行转账</option>
                  <option>PayPal</option>
                </select>
              </div>
              <div>
                <label className="text-gray-600 text-xs font-medium block mb-1.5">提现金额</label>
                <input
                  type="number"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 outline-none"
                />
              </div>
            </div>
            <button
              onClick={handleWithdraw}
              className={`w-full py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${
                withdrawSuccess
                  ? "bg-green-500 text-white"
                  : "bg-amber-500 hover:bg-amber-600 text-white"
              }`}
            >
              {withdrawSuccess ? (
                <>
                  <i className="ri-check-line mr-1"></i>提现申请已提交！
                </>
              ) : (
                <>
                  <i className="ri-bank-card-line mr-1"></i>确认提现
                </>
              )}
            </button>
            <p className="text-gray-400 text-xs text-center mt-3">预计 1–3 个工作日到账</p>
          </div>
        </div>
      )}
    </div>
  );
}
